import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Video, Trash2, Calendar, Clock, MapPin, Check, HelpCircle } from 'lucide-react';
import * as cal from '../../api/calendar';
import ConfirmDialog, { ConfirmState } from '../ConfirmDialog';

interface Props {
  event: cal.CalendarEvent;
  onClose: () => void;
  onChanged: () => void;
}

export default function EventModal({ event, onClose, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const isOwner = true;

  const respond = async (r: cal.CalendarEvent['response']) => {
    setBusy(true);
    try {
      await cal.respondEvent(event.id, r);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    setConfirm({
      title: 'Delete this event?',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        setBusy(true);
        try {
          await cal.deleteEvent(event.id);
          onChanged();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const start = new Date(event.start_at);
  const end = new Date(event.end_at);
  const dateLabel = start.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const timeLabel = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  const isMeeting = event.source_type === 'chat_meeting';
  const accentBar = isMeeting ? 'bg-blue-500' : 'bg-emerald-500';
  const accentChip = isMeeting
    ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
    : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300';

  return (
    <>
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200 dark:border-gray-700"
        >
          {/* Accent bar */}
          <div className={`h-1.5 ${accentBar}`} />

          <div className="p-6">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <span
                  className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 ${accentChip}`}
                >
                  {isMeeting ? 'Meeting' : 'Event'}
                </span>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">
                  {event.title}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Meta */}
            <ul className="space-y-2.5 mb-4 text-sm">
              <li className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                <Calendar size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span>{dateLabel}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                <Clock size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span>{timeLabel}</span>
              </li>
              {event.location && (
                <li className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                  <MapPin size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span>{event.location}</span>
                </li>
              )}
            </ul>

            {/* Description */}
            {event.description && (
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 mb-4">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                  {event.description}
                </p>
              </div>
            )}

            {/* Meet link */}
            {event.meet_url && (
              <a
                href={event.meet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 mb-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
              >
                <Video size={16} />
                Join Google Meet
              </a>
            )}

            {/* RSVP */}
            {isMeeting && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Your response
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { key: 'ACCEPTED', label: 'Yes', icon: Check, activeCls: 'bg-emerald-600 text-white border-emerald-600' },
                    { key: 'TENTATIVE', label: 'Maybe', icon: HelpCircle, activeCls: 'bg-amber-500 text-white border-amber-500' },
                    { key: 'DECLINED', label: 'No', icon: X, activeCls: 'bg-red-600 text-white border-red-600' },
                  ] as const).map(({ key, label, icon: Icon, activeCls }) => {
                    const isActive = event.response === key;
                    return (
                      <button
                        key={key}
                        onClick={() => respond(key)}
                        disabled={busy}
                        className={
                          'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ' +
                          (isActive
                            ? activeCls
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700')
                        }
                      >
                        <Icon size={13} />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Delete (manual events only) */}
            {isOwner && event.source_type === 'manual' && (
              <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={del}
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  Delete event
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}
